import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Slice, CmsBlock, CmsBlockSlice } from './types'
import { PWAThunk } from 'lib/core/data'

export const initialState: Slice = {}

interface ActionData {
  id: string
  data?: CmsBlock
}

const makeSlice = (slice: CmsBlockSlice): CmsBlockSlice => {
  return slice ?? ({
    isFetching: false,
    isSuccess: false,
    isFailure: false,
    updatedAt: undefined,
    payload: undefined
  })
}

const slice = createSlice({
  name: 'cms/cmsBlock',
  initialState,
  reducers: {
    request (state, action: PayloadAction<ActionData>) {
      const { id } = action.payload
      const slice = makeSlice(state[id])

      slice.isFetching = true
      state[id] = slice
    },

    success (state, action: PayloadAction<ActionData>) {
      const { id, data } = action.payload
      const slice = makeSlice(state[id])

      slice.isFetching = false
      slice.isSuccess = true
      slice.isFailure = false
      slice.updatedAt = Date.now()
      slice.payload = data
      state[id] = slice
    },

    failure (state, action: PayloadAction<ActionData>) {
      const { id } = action.payload
      const slice = makeSlice(state[id])

      slice.isFetching = false
      slice.isSuccess = false
      slice.isFailure = true
      slice.updatedAt = Date.now()
      slice.payload = undefined
      state[id] = slice
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure } = slice.actions

export function fetchCmsBlock (id: string): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    dispatch(request({
      id
    }))
    try {
      const response = await magentoClient.getClient().cms.fetchCmsBlock({ id })
      const { data } = response.data
      const cmsBlockData = data.cmsBlocks.items[0]
      if (cmsBlockData) {
        dispatch(success({
          id,
          data: cmsBlockData
        }))
      } else {
        dispatch(failure({
          id
        }))
      }
      return response
    } catch (e) {
      dispatch(failure({
        id
      }))
    }
  }
}

export function fetchCmsBlocks (ids: string[]): PWAThunk {
  return async (dispatch, getState, magentoData) => {
    const { magentoClient } = magentoData
    ids.forEach(id => dispatch(request({
      id
    })))
    const cmsBlocksIndex: Record<string, CmsBlock> = {}
    const response = await magentoClient.getClient().cms.fetchCmsBlock({ id: ids })
    const { data } = response.data

    data.cmsBlocks.items.forEach((block: CmsBlock) => {
      if (block) {
        cmsBlocksIndex[block.identifier] = block
      }
    })

    ids.forEach(id => {
      const cmsBlockData = cmsBlocksIndex[id]
      if (cmsBlockData) {
        dispatch(success({
          id,
          data: cmsBlockData
        }))
      } else {
        dispatch(failure({
          id
        }))
      }
    })

    return response
  }
}
